import type { MessageKey } from './messages.en.ts';

/**
 * The `t()` a component receives from `useT()`. Declared here, in a module with no JSX, rather than
 * in `i18n.tsx` where the hook lives: a pure `.ts` unit that wants to take a `t` as a parameter
 * (`hotkeys.ts`, `shortcutSections.ts`) must not name a `.tsx` file even in a type position, because
 * this project's unit tests run under vitest's node environment, which cannot load JSX at all.
 */
export type TFunction = (key: MessageKey, params?: Record<string, string | number>) => string;

/**
 * A sentence the caller has not written yet: which dictionary entry, and what goes in its slots.
 *
 * The other half of the same problem `TFunction` solves. A pure `.ts` module that decides WHICH
 * sentence to show — but is tested under node, with no provider to resolve it — hands back one of
 * these and lets its render site do the lookup (`resultSummary.ts`, `libraryActions.ts`). Take a
 * `t` instead in either of two cases: the return value is a nested structure the caller would
 * otherwise have to walk just to resolve it (`facetAdapter.ts`, `hotkeys.ts`), or some branch of it
 * returns text that is not copy at all — a raw action name from a newer server, an error sentence
 * the API wrote (`audit.ts`, `ingestQueue.ts`). A `Message` has no way to say "this string,
 * verbatim", and inventing a passthrough key to fake one would put untranslatable text into the
 * dictionary's own vocabulary.
 */
export interface Message {
  key: MessageKey;
  params?: Record<string, string | number>;
}

/**
 * MVP-272 Phase 0. Pure interpolation and dictionary lookup, kept out of `i18n.tsx` so both are
 * unit-testable without touching React, a DOM, or `import.meta.env` at all.
 *
 * Placeholder syntax: `{name}` inside a dictionary string is replaced with `String(params.name)`.
 * A placeholder with no matching key in `params` — a typo in the dictionary, or a param the caller
 * forgot to pass — is left in the output exactly as written (`{name}`), never blanked: a visibly
 * broken placeholder in a live show is a bug the KJ can point at immediately, while a silently
 * empty one is a bug that only gets noticed on a second, closer read, if at all.
 */
export function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match
  );
}

/**
 * `isDev` is a plain parameter rather than read from `import.meta.env` in here, so this function
 * stays callable directly from `i18n.test.ts` with either value — the actual dev/prod switch lives
 * in `i18n.tsx`'s `useT()`, the one place that legitimately knows which mode the app is running in.
 *
 * A missing key throws in development (loud, at the call site, impossible to miss) and degrades to
 * the raw key in production (visibly wrong text beats a blank one, and a thrown error from a
 * translation lookup has no business taking down a live show over a copy mistake).
 */
export function translate(
  dict: Record<string, string>,
  key: string,
  params: Record<string, string | number> | undefined,
  isDev: boolean
): string {
  const template = dict[key];
  if (template === undefined) {
    if (isDev) throw new Error(`i18n: missing key "${key}" in the active dictionary`);
    return key;
  }
  return interpolate(template, params);
}
